import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  MessageCircle, 
  Heart, 
  Flag, 
  Plus, 
  Users, 
  Shield, 
  AlertTriangle,
  Clock,
  User,
  Eye,
  Pin,
  Lock,
  Trash2
} from 'lucide-react';
import api from '../utils/api';

const ForumDetail = () => {
  const { forumId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newPostData, setNewPostData] = useState({
    title: '',
    content: '',
    category: 'general',
    priority: 'medium',
    tags: '',
    isAnonymous: false,
    authorName: ''
  });
  const [newReplyData, setNewReplyData] = useState({
    content: '',
    isAnonymous: false,
    authorName: ''
  });
  const [selectedPostData, setSelectedPostData] = useState(null);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedForum, setSelectedForum] = useState(null);
  
  // Now we can safely use selectedForum after it's declared
  const forum = location.state?.forum || selectedForum;

  console.log('=== FORUM DETAIL DEBUG ===');
  console.log('Current location pathname:', location.pathname);
  console.log('forumId from params:', forumId);
  console.log('forum from location state:', location.state?.forum);
  console.log('selectedForum state:', selectedForum);
  console.log('final forum object:', forum);

  useEffect(() => {
    if (forum) {
      fetchPosts();
    } else if (forumId && !selectedForum) {
      // If no forum data in state, fetch it from the API
      fetchForumData();
    }
    // Get current user info
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser(payload);
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }, [forum, forumId, selectedForum]);

  const fetchForumData = async () => {
    try {
      console.log('Fetching forum data for forumId:', forumId);
      console.log('Token present:', !!localStorage.getItem('token'));
      setLoading(true);
      const response = await api.get(`/peer-support/forums/${forumId}`);
      console.log('Forum data response:', response.data);
      setSelectedForum(response.data.forum);
      fetchPosts();
    } catch (err) {
      console.error('Error fetching forum data:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load forum data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      console.log('Fetching posts for forumId:', forumId);
      console.log('Token present:', !!localStorage.getItem('token'));
      setLoading(true);
      const response = await api.get(`/peer-support/forums/${forumId}/posts`);
      console.log('Posts response:', response.data);
      setPosts(response.data.posts);
    } catch (err) {
      console.error('Error fetching posts:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load forum posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      const postData = {
        ...newPostData,
        tags: newPostData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      await api.post(`/peer-support/forums/${forumId}/posts`, postData);

      setShowNewPost(false);
      setNewPostData({
        title: '',
        content: '',
        category: 'general',
        priority: 'medium',
        tags: '',
        isAnonymous: false,
        authorName: ''
      });
      fetchPosts();
      
      // Refresh forum data to update statistics
      if (forumId) {
        try {
          const response = await api.get(`/peer-support/forums/${forumId}`);
          setSelectedForum(response.data.forum);
        } catch (refreshErr) {
          console.error('Error refreshing forum data:', refreshErr);
        }
      }
    } catch (err) {
      console.error('Error creating post:', err);
      alert(err.response?.data?.error || 'Failed to create post');
    }
  };

  const handleCreateReply = async (postId) => {
    try {
      await api.post(`/peer-support/posts/${postId}/replies`, newReplyData);

      setNewReplyData({
        content: '',
        isAnonymous: false,
        authorName: ''
      });
      
      // Refresh the selected post data to show the new reply
      if (selectedPostData && selectedPostData.id === postId) {
        fetchPostWithReplies(postId);
      }
      
      // Refresh forum data to update statistics
      if (forumId) {
        try {
          const response = await api.get(`/peer-support/forums/${forumId}`);
          setSelectedForum(response.data.forum);
        } catch (refreshErr) {
          console.error('Error refreshing forum data:', refreshErr);
        }
      }
    } catch (err) {
      console.error('Error creating reply:', err);
      alert(err.response?.data?.error || 'Failed to create reply');
    }
  };

  const fetchPostWithReplies = async (postId) => {
    try {
      setLoadingReplies(true);
      const response = await api.get(`/peer-support/posts/${postId}`);
      setSelectedPostData(response.data.post);
    } catch (err) {
      console.error('Error fetching post with replies:', err);
      alert('Failed to load replies');
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleViewReplies = async (postId) => {
    if (selectedPost === postId) {
      // If already selected, deselect it
      setSelectedPost(null);
      setSelectedPostData(null);
    } else {
      // Select the post and fetch its replies
      setSelectedPost(postId);
      await fetchPostWithReplies(postId);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      await api.post(`/peer-support/posts/${postId}/like`, {});
      fetchPosts();
    } catch (err) {
      console.error('Error liking post:', err);
    }
  };

  const handleFlagPost = async (postId, reason) => {
    try {
      await api.post(`/peer-support/posts/${postId}/flag`, {
        reason,
        description: 'Flagged by user'
      });
      alert('Post flagged successfully');
    } catch (err) {
      console.error('Error flagging post:', err);
      alert(err.response?.data?.error || 'Failed to flag post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/peer-support/posts/${postId}`);

      // Remove the post from the list
      setPosts(posts.filter(post => post.id !== postId));
      
      // Refresh forum data to update statistics
      if (forumId) {
        try {
          const response = await api.get(`/peer-support/forums/${forumId}`);
          setSelectedForum(response.data.forum);
        } catch (refreshErr) {
          console.error('Error refreshing forum data:', refreshErr);
        }
      }
      
      alert('Post deleted successfully.');
    } catch (err) {
      console.error('Error deleting post:', err);
      alert(err.response?.data?.error || 'Failed to delete post');
    }
  };

  const handleDeleteReply = async (postId, replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/peer-support/posts/${postId}/replies/${replyId}`);

      // Update the selected post data to remove the deleted reply
      if (selectedPostData && selectedPostData.replies) {
        setSelectedPostData({
          ...selectedPostData,
          replies: selectedPostData.replies.filter(reply => reply.id !== replyId)
        });
      }

      // Refresh forum data to update statistics
      if (forumId) {
        try {
          const response = await api.get(`/peer-support/forums/${forumId}`);
          setSelectedForum(response.data.forum);
        } catch (refreshErr) {
          console.error('Error refreshing forum data:', refreshErr);
        }
      }

      alert('Reply deleted successfully.');
    } catch (err) {
      console.error('Error deleting reply:', err);
      alert(err.response?.data?.error || 'Failed to delete reply');
    }
  };

  const handleLeaveForum = async () => {
    if (!window.confirm(`Are you sure you want to leave the "${forum.name}" forum? You can rejoin anytime.`)) {
      return;
    }

    try {
      await api.post(`/peer-support/forums/${forumId}/leave`);
      
      // Navigate back to peer support page
      navigate('/app/peer-support');
    } catch (err) {
      console.error('Error leaving forum:', err);
      alert(err.response?.data?.error || 'Failed to leave forum. Please try again.');
    }
  };

  const canDeletePost = (post) => {
    if (!currentUser) return false;
    
    // Debug logging
    console.log('Checking delete permission for post:', {
      postUserId: post.userId,
      currentUserId: currentUser.userId,
      postUserIdType: typeof post.userId,
      currentUserIdType: typeof currentUser.userId,
      isAdvanced: currentUser.subscription?.plan === 'advanced'
    });
    
    // Check if user is the post owner or has advanced plan
    const isOwner = post.userId === currentUser.userId || 
                   post.userId === currentUser._id ||
                   (post.user && post.user.id === currentUser.userId) ||
                   (post.user && post.user._id === currentUser.userId);
    const isAdvanced = currentUser.subscription?.plan === 'advanced';
    
    return isOwner || isAdvanced;
  };

  const canDeleteReply = (reply) => {
    if (!currentUser) return false;
    
    // Debug logging
    console.log('Checking delete permission for reply:', {
      replyUserId: reply.userId,
      currentUserId: currentUser.userId,
      replyUserIdType: typeof reply.userId,
      currentUserIdType: typeof currentUser.userId,
      isAdvanced: currentUser.subscription?.plan === 'advanced'
    });
    
    // Check if user is the reply owner or has advanced plan
    const isOwner = reply.userId === currentUser.userId || 
                   reply.userId === currentUser._id ||
                   (reply.user && reply.user.id === currentUser.userId) ||
                   (reply.user && reply.user._id === currentUser.userId);
    const isAdvanced = currentUser.subscription?.plan === 'advanced';
    
    return isOwner || isAdvanced;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-orange-100 text-orange-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'general': 'bg-gray-100 text-gray-800',
      'question': 'bg-blue-100 text-blue-800',
      'support': 'bg-green-100 text-green-800',
      'experience': 'bg-purple-100 text-purple-800',
      'advice': 'bg-yellow-100 text-yellow-800',
      'crisis': 'bg-red-100 text-red-800',
      'celebration': 'bg-pink-100 text-pink-800'
    };
    return colors[category] || colors.general;
  };

  if (!forum) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Forum Not Found</h2>
          <p className="text-gray-600 mb-4">The forum you're looking for could not be found or loaded.</p>
          <button
            onClick={() => navigate('/app/peer-support')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Peer Support
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
            <h3 className="text-lg font-semibold text-red-800">Error Loading Forum</h3>
          </div>
          <p className="text-red-700 mt-2">{error}</p>
          <button 
            onClick={fetchPosts}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/app/peer-support')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Peer Support
        </button>
        
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">{forum.name}</h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1" />
                <span>{forum.stats.totalMembers.toLocaleString()} members</span>
              </div>
              <div className="flex items-center">
                <MessageCircle className="h-4 w-4 mr-1" />
                <span>{forum.stats.totalPosts.toLocaleString()} posts</span>
              </div>
            </div>
          </div>
          <p className="text-gray-700 mb-4">{forum.description}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-green-600" />
              <span className="text-sm text-gray-600">Moderated Environment</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleLeaveForum}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center"
              >
                Leave Forum
              </button>
              <button
                onClick={() => setShowNewPost(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Post
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* New Post Form */}
      {showNewPost && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Post</h2>
          <form onSubmit={handleCreatePost} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={newPostData.title}
                onChange={(e) => setNewPostData({...newPostData, title: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                value={newPostData.content}
                onChange={(e) => setNewPostData({...newPostData, content: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 h-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newPostData.category}
                  onChange={(e) => setNewPostData({...newPostData, category: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">General</option>
                  <option value="question">Question</option>
                  <option value="support">Support</option>
                  <option value="experience">Experience</option>
                  <option value="advice">Advice</option>
                  <option value="celebration">Celebration</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={newPostData.priority}
                  onChange={(e) => setNewPostData({...newPostData, priority: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                value={newPostData.tags}
                onChange={(e) => setNewPostData({...newPostData, tags: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., anxiety, coping, support"
              />
            </div>
            
            {forum.settings.allowAnonymous && (
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newPostData.isAnonymous}
                    onChange={(e) => setNewPostData({...newPostData, isAnonymous: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Post anonymously</span>
                </label>
                
                {newPostData.isAnonymous && (
                  <input
                    type="text"
                    value={newPostData.authorName}
                    onChange={(e) => setNewPostData({...newPostData, authorName: e.target.value})}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Display name (optional)"
                  />
                )}
              </div>
            )}
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowNewPost(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Post
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Posts List */}
      <div className="space-y-6">
        {posts.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
            <p className="text-gray-600 mb-4">Be the first to start a discussion in this forum!</p>
            <button
              onClick={() => setShowNewPost(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Create First Post
            </button>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl shadow-lg p-6">
              {/* Post Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">
                        {post.isAnonymous ? (post.authorName || 'Anonymous') : post.author.name}
                      </span>
                      {post.isPinned && <Pin className="h-4 w-4 text-blue-600" />}
                      {post.isLocked && <Lock className="h-4 w-4 text-red-600" />}
                      {post.crisisDetected && <AlertTriangle className="h-4 w-4 text-red-600" />}
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                      <Eye className="h-3 w-3" />
                      <span>{post.viewCount} views</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                    {post.category}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(post.priority)}`}>
                    {post.priority}
                  </span>
                </div>
              </div>

              {/* Post Content */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                
                {post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {post.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Post Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleLikePost(post.id)}
                    className={`flex items-center space-x-1 px-3 py-1 rounded-lg transition-colors ${
                      post.isLiked 
                        ? 'bg-red-100 text-red-600' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Heart className={`h-4 w-4 ${post.isLiked ? 'fill-current' : ''}`} />
                    <span>{post.likes}</span>
                  </button>
                  
                  <button
                    onClick={() => handleViewReplies(post.id)}
                    className="flex items-center space-x-1 px-3 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>{post.replyCount} replies</span>
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleFlagPost(post.id, 'inappropriate')}
                    className="text-gray-400 hover:text-red-600"
                    title="Flag post"
                  >
                    <Flag className="h-4 w-4" />
                  </button>
                  {/* Temporarily show delete button for testing */}
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="text-gray-400 hover:text-red-600"
                    title="Delete post"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Reply Section */}
              {selectedPost === post.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  {/* Display Replies */}
                  {loadingReplies ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                  ) : selectedPostData && selectedPostData.replies && selectedPostData.replies.length > 0 ? (
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-3">Replies ({selectedPostData.replies.length})</h4>
                      <div className="space-y-4">
                        {selectedPostData.replies.map((reply) => (
                          <div key={reply.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                  <User className="h-3 w-3 text-gray-600" />
                                </div>
                                <span className="font-medium text-sm text-gray-900">
                                  {reply.isAnonymous ? (reply.authorName || 'Anonymous') : reply.author.name}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">
                                  {new Date(reply.createdAt).toLocaleDateString()}
                                </span>
                                {/* Temporarily show delete button for testing */}
                                <button
                                  onClick={() => handleDeleteReply(post.id, reply.id)}
                                  className="text-gray-400 hover:text-red-600"
                                  title="Delete reply"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">{reply.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : selectedPostData && (
                    <div className="mb-6 text-center py-4">
                      <p className="text-gray-500 text-sm">No replies yet. Be the first to reply!</p>
                    </div>
                  )}

                  {/* Add Reply Form */}
                  <h4 className="font-medium text-gray-900 mb-3">Add Reply</h4>
                  <div className="space-y-3">
                    <textarea
                      value={newReplyData.content}
                      onChange={(e) => setNewReplyData({...newReplyData, content: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Write your reply..."
                    />
                    
                    {forum.settings.allowAnonymous && (
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newReplyData.isAnonymous}
                            onChange={(e) => setNewReplyData({...newReplyData, isAnonymous: e.target.checked})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Reply anonymously</span>
                        </label>
                        
                        {newReplyData.isAnonymous && (
                          <input
                            type="text"
                            value={newReplyData.authorName}
                            onChange={(e) => setNewReplyData({...newReplyData, authorName: e.target.value})}
                            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Display name (optional)"
                          />
                        )}
                      </div>
                    )}
                    
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleCreateReply(post.id)}
                        disabled={!newReplyData.content.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ForumDetail; 